import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Feature, Map, View } from "ol";
import TileLayer from "ol/layer/Tile.js";
import { OSM } from "ol/source.js";
import { fromLonLat, toLonLat } from "ol/proj.js";
import styles from "./Mapa.module.scss";
import Sidebar from "./SideBar.tsx";
import Point from "ol/geom/Point.js";
import LineString from "ol/geom/LineString.js";
import { getLocais, getRoutes, SavedRoute } from "../../services/api.tsx";
import Style from "ol/style/Style.js";
import Icon from "ol/style/Icon.js";
import Text from "ol/style/Text.js";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector.js";
import  Estabelecimento from '../../domain/Estabelecimentos.tsx';
import Geolocation from "ol/Geolocation.js";
import { Circle as CircleStyle, Fill, Stroke } from "ol/style.js";
import StoreDetailsModal from "../../components/StoreDetailsModal/StoreDetailsModal.tsx";

const topFilterPills = [
  { id: "restaurantes", label: "🍽️ Restaurantes" },
  { id: "hoteis", label: "🛏️ Hotéis" },
  { id: "praia", label: "🏖️ Praias" },
  { id: "artesanato", label: "🏺 Artesanato" },
  { id: "centros-historicos", label: "🏛️ Centros Históricos" },
  { id: "ecoturismo", label: "🌿 Ecoturismo" },
];

function Mapa() {
  const mapElement = useRef<HTMLDivElement | null>(null);
  const [mapInstance, setMapInstance] = useState<Map | undefined>();

  const vectorSourceRef = useRef(new VectorSource());

  const routeLineSourceRef = useRef(new VectorSource());
  const routePointSourceRef = useRef(new VectorSource());
  const userLocationSourceRef = useRef(new VectorSource());

  const routeStepsRef = useRef<Array<{ lon: number; lat: number; label: string; order: number }>>([]);
  const geolocationRef = useRef<Geolocation | null>(null);

  const parseCoord = (value: any): number | null => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [locaisData, setLocaisData] = useState<any[]>([]);
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [savedRoutes, setSavedRoutes] = useState<SavedRoute[]>([]);
  const [selectedSavedRouteId, setSelectedSavedRouteId] = useState<string | null>(null);
  const [hoveredStore, setHoveredStore] = useState<any>(null);
  const [modalPosition, setModalPosition] = useState<{ left: number; top: number } | null>(null);
  const [isModalHovered, setIsModalHovered] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

    var estabelecimentos: Estabelecimento[];

  useEffect(() => {
    if (!mapElement.current) return;

    const initialMap = new Map({
      target: mapElement.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        new VectorLayer({ source: vectorSourceRef.current }),
        new VectorLayer({ source: routeLineSourceRef.current, zIndex: 12 }),
        new VectorLayer({ source: routePointSourceRef.current, zIndex: 15 }),
        new VectorLayer({
          source: userLocationSourceRef.current,
          zIndex: 10,
        }),
      ],
      view: new View({
        center: fromLonLat([-37.0715, -10.9472]),
        zoom: 13,
      }),
      controls: [],
    });


    loadPoints().then(async () => {
      const featuresLoaded = vectorSourceRef.current.getFeatures();
      const featuresProps = featuresLoaded.map((f) => {
        const p = { ...f.getProperties() } as any;
        delete p.geometry;
        return p;
      });
      setLocaisData(featuresProps);
      await loadSavedRoutes(initialMap);

      initialMap.on("singleclick", (evt) => {
        const feature = initialMap.forEachFeatureAtPixel(evt.pixel, (feat) => feat);
        if (feature) {
          const props = { ...feature.getProperties() } as any;
          console.log("Feature clicada:", props);
        }
      });
    });

    initialMap.on("pointermove", (evt) => {
      if (evt.dragging) {
        setHoveredStore(null);
        setModalPosition(null);
        return;
      }

      const feature = initialMap.forEachFeatureAtPixel(evt.pixel, (feat) =>
        feat.get("isVisible") === false ? undefined : feat,
      );
      const viewport = initialMap.getTargetElement();

      if (feature) {
        if (hideTimeoutRef.current) {
          window.clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }

        const props = { ...feature.getProperties() } as any;
        if (props && props.nome) {
          const store = { ...props } as any;
          delete (store as any).geometry;
          setHoveredStore(store);
          const [x, y] = evt.pixel as [number, number];
          setModalPosition({ left: x + 18, top: y + 18 });
          if (viewport) {
            viewport.style.cursor = "pointer";
          }
          return;
        }
      }

      if (isModalHovered) {
        return;
      }

      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setHoveredStore(null);
        setModalPosition(null);
        if (viewport) {
          viewport.style.cursor = "default";
        }
        hideTimeoutRef.current = null;
      }, 250);
    });

    setMapInstance(initialMap);

    

    const geolocation = new Geolocation({
      trackingOptions: {
        enableHighAccuracy: true,
      },
      projection: initialMap.getView().getProjection(),
    });

    geolocationRef.current = geolocation;

    const positionFeature = new Feature();
    positionFeature.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#3399CC" }),
          stroke: new Stroke({ color: "#fff", width: 3 }),
        }),
      }),
    );

    geolocation.on("change:position", function () {
      const coordinates = geolocation.getPosition();
      if (coordinates) {
        positionFeature.setGeometry(new Point(coordinates));
        if (routeStepsRef.current.length > 0) {
          updateRouteFromUserLocation(coordinates, initialMap, routeStepsRef.current);
        }
      }
    });

    userLocationSourceRef.current.addFeature(positionFeature);

    

    geolocation.setTracking(true);

    return () => {
      initialMap.setTarget(undefined);
      geolocation.setTracking(false);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstance) return;

    mapInstance.updateSize();
    const resizeTimer = window.setTimeout(() => {
      mapInstance.updateSize();
    }, 320);

    return () => window.clearTimeout(resizeTimer);
  }, [isSidebarOpen, mapInstance]);

  const handleZoomIn = () => {
    if (mapInstance) {
      const view = mapInstance.getView();
      const zoom = view.getZoom() || 0;
      view.setZoom(zoom + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstance) {
      const view = mapInstance.getView();
      const zoom = view.getZoom() || 0;
      view.setZoom(zoom - 1);
    }
  };

  const handleGoToMyLocation = () => {
    if (mapInstance && geolocationRef.current) {
      const position = geolocationRef.current.getPosition();
      if (position) {
        mapInstance.getView().animate({
          center: position,
          zoom: 15,
          duration: 1000,
        });
      } else {
        alert(
          "Ainda estamos buscando sua localização ou a permissão foi negada.",
        );
      }
    }
  };

  const normalizeCategoryText = (local: any): string => {
    if (typeof local.categoria === "string") {
      return local.categoria;
    }
    if (Array.isArray(local.categoria)) {
      return local.categoria
        .map((item: any) => item?.nomeCategoria)
        .filter(Boolean)
        .join(" ");
    }
    if (Array.isArray(local.categorias)) {
      return local.categorias
        .map((item: any) => item?.nomeCategoria)
        .filter(Boolean)
        .join(" ");
    }
    return "";
  };

  const matchesFilterId = (local: any, filterId: string | null) => {
    if (!filterId) return true;

    const text = [
      normalizeCategoryText(local),
      local.descricao,
      local.cidade,
      Array.isArray(local.categorias)
        ? local.categorias.map((item: any) => item?.nomeCategoria).join(" ")
        : "",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    switch (filterId) {
      case "restaurantes":
        return text.includes("restaurante") || text.includes("comida");
      case "hoteis":
        return text.includes("hotel") || text.includes("pousada");
      case "pousadas":
        return text.includes("pousada");
      case "comida-tipica":
        return (
          text.includes("comida típica") ||
          text.includes("comida tipica") ||
          text.includes("típico") ||
          text.includes("tipico")
        );
      case "praia":
        return (
          text.includes("praia") ||
          text.includes("beira-mar") ||
          text.includes("beira mar")
        );
      case "economico":
        return text.includes("econômico") || text.includes("economico");
      case "ecoturismo":
        return (
          text.includes("ecoturismo") ||
          text.includes("natureza") ||
          text.includes("trilha") ||
          text.includes("cânion") ||
          text.includes("canion")
        );
      case "artesanato":
        return text.includes("artesanato");
      case "centros-historicos":
        return (
          text.includes("histórico") ||
          text.includes("historico") ||
          text.includes("centro histórico") ||
          text.includes("centro historico")
        );
      case "cultura-festas":
        return text.includes("cultura") || text.includes("festa") || text.includes("festival");
      case "pontos-turisticos":
        return local.avaliacao === undefined || local.avaliacao === null;
      default:
        return true;
    }
  };

  const updateFeatureVisibility = (filterId: string | null) => {
    const features = vectorSourceRef.current.getFeatures();
    features.forEach((feature) => {
      const props = { ...feature.getProperties() } as any;
      const visible = matchesFilterId(props, filterId);
      if (visible) {
        const markerStyle = feature.get("markerStyle");
        feature.setStyle(markerStyle || feature.getStyle());
        feature.set("isVisible", true);
      } else {
        feature.setStyle(new Style());
        feature.set("isVisible", false);
      }
    });
  };

  useEffect(() => {
    if (!mapInstance) return;
    updateFeatureVisibility(selectedFilterId);
  }, [selectedFilterId, mapInstance]);

  const handleSidebarItemHover = (store: any) => {
    if (!mapInstance || !store.latitude || !store.longitude) return;

    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const coordinate = fromLonLat([store.longitude, store.latitude]);
    const pixel = mapInstance.getPixelFromCoordinate(coordinate);
    setHoveredStore(store);
    setModalPosition({ left: pixel[0] + 18, top: pixel[1] + 18 });
  };

  const handleSidebarItemLeave = () => {
    setIsModalHovered(false);
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = window.setTimeout(() => {
      setHoveredStore(null);
      setModalPosition(null);
      hideTimeoutRef.current = null;
    }, 250);
  };

  const navigate = useNavigate();

  const handleTopFilterClick = (filterId: string) => {
    setSelectedFilterId((currentFilter) =>
      currentFilter === filterId ? null : filterId,
    );
    setIsSidebarOpen(true);
  };

    const updateRouteFromUserLocation = async (
    coordinates: number[],
    map: Map,
    stepCoords: Array<{ lon: number; lat: number; label: string; order: number }>,
  ) => {
    if (stepCoords.length < 1) return;

    const userLonLat = toLonLat(coordinates);
    const waypoints = stepCoords.map((step) => `${step.lon},${step.lat}`);
    const coordinateString = `${userLonLat[0]},${userLonLat[1]};${waypoints.join(';')}`;

    try {
      const routeUrl = `https://router.project-osrm.org/route/v1/driving/${coordinateString}?overview=full&geometries=geojson&annotations=distance,duration`;
      const response = await fetch(routeUrl);
      const data = await response.json();
      if (data?.routes?.length) {
        const routeGeo = data.routes[0].geometry;
        const mapped = (routeGeo.coordinates as number[][]).map(([lon, lat]) => fromLonLat([lon, lat]));
        const routeFeature = new Feature({ geometry: new LineString(mapped as any) });
        routeFeature.setStyle(
          new Style({ stroke: new Stroke({ color: '#1d4ed8', width: 5 }) }),
        );
        routeLineSourceRef.current.clear();
        routeLineSourceRef.current.addFeature(routeFeature);
        const extent = routeFeature.getGeometry()?.getExtent();
        if (extent) {
          map.getView().fit(extent, { padding: [70, 70, 70, 70], maxZoom: 16, duration: 500 });
        }
      }
    } catch (error) {
      console.error('Falha ao carregar rota OSRM:', error);
      const straight = new LineString([
        userLonLat,
        ...stepCoords.map((step) => [step.lon, step.lat]),
      ].map(([lon, lat]) => fromLonLat([lon, lat])) as any);
      const fallbackRoute = new Feature({ geometry: straight });
      fallbackRoute.setStyle(new Style({ stroke: new Stroke({ color: '#1d4ed8', width: 4, lineDash: [8, 8] }) }));
      routeLineSourceRef.current.clear();
      routeLineSourceRef.current.addFeature(fallbackRoute);
      const extent = straight.getExtent();
      if (extent) {
        map.getView().fit(extent, { padding: [70, 70, 70, 70], maxZoom: 16, duration: 500 });
      }
    }
  };

  const updateRouteForStepSequence = async (
    map: Map,
    stepCoords: Array<{ lon: number; lat: number; label: string; order: number }>,
  ) => {
    if (stepCoords.length < 2) return;

    const waypoints = stepCoords.map((step) => `${step.lon},${step.lat}`);
    const coordinateString = waypoints.join(';');

    try {
      const routeUrl = `https://router.project-osrm.org/route/v1/driving/${coordinateString}?overview=full&geometries=geojson&annotations=distance,duration`;
      const response = await fetch(routeUrl);
      const data = await response.json();
      if (data?.routes?.length) {
        const routeGeo = data.routes[0].geometry;
        const mapped = (routeGeo.coordinates as number[][]).map(([lon, lat]) => fromLonLat([lon, lat]));
        const routeFeature = new Feature({ geometry: new LineString(mapped as any) });
        routeFeature.setStyle(new Style({ stroke: new Stroke({ color: '#1d4ed8', width: 5 }) }));
        routeLineSourceRef.current.clear();
        routeLineSourceRef.current.addFeature(routeFeature);
        const extent = routeFeature.getGeometry()?.getExtent();
        if (extent) {
          map.getView().fit(extent, { padding: [70, 70, 70, 70], maxZoom: 16, duration: 500 });
        }
        return;
      }
    } catch (error) {
      console.error('Falha ao carregar rota OSRM (sequence):', error);
    }

    const straight = new LineString(stepCoords.map((step) => fromLonLat([step.lon, step.lat])) as any);
    const fallbackRoute = new Feature({ geometry: straight });
    fallbackRoute.setStyle(new Style({ stroke: new Stroke({ color: '#1d4ed8', width: 4, lineDash: [8, 8] }) }));
    routeLineSourceRef.current.clear();
    routeLineSourceRef.current.addFeature(fallbackRoute);
    const extent = straight.getExtent();
    if (extent) {
      map.getView().fit(extent, { padding: [70, 70, 70, 70], maxZoom: 16, duration: 500 });
    }
  };

  const getSavedRouteKey = (route: SavedRoute, index: number) => route.id ?? `local-${index}`;

  const loadRouteToMap = async (map: Map, routeData: SavedRoute) => {
    if (!routeData || !routeData.steps?.length) {
      return;
    }

    const stepCoords = routeData.steps
      .map((step, index) => {
        const lon = parseCoord(step.longitude);
        const lat = parseCoord(step.latitude);
        return lon !== null && lat !== null
          ? { lon, lat, label: step.nome, order: index + 1 }
          : null;
      })
      .filter((item): item is { lon: number; lat: number; label: string; order: number } => item !== null);

    if (stepCoords.length < 1) return;

    routeStepsRef.current = stepCoords;

    const markerFeatures = stepCoords.map((step) => {
      const feature = new Feature({
        geometry: new Point(fromLonLat([step.lon, step.lat])),
      });
      feature.setStyle(
        new Style({
          image: new CircleStyle({
            radius: 12,
            fill: new Fill({ color: '#1d4ed8' }),
            stroke: new Stroke({ color: '#ffffff', width: 2 }),
          }),
          text: new Text({
            text: String(step.order),
            fill: new Fill({ color: '#fff' }),
            font: 'bold 12px Arial',
            offsetY: 0,
          }),
        }),
      );
      feature.setProperties({ label: step.label, order: step.order });
      return feature;
    });
    routePointSourceRef.current.clear();
    routePointSourceRef.current.addFeatures(markerFeatures);

    const geolocation = geolocationRef.current?.getPosition();
    if (geolocation) {
      updateRouteFromUserLocation(geolocation, map, stepCoords);
    } else if (stepCoords.length > 1) {
      updateRouteForStepSequence(map, stepCoords);
    }
  };

  const loadSavedRoutes = async (map: Map) => {
    let routes: SavedRoute[] = [];
    let activeRoute: SavedRoute | null = null;

    try {
      routes = await getRoutes();
    } catch {
      // ignore, fallback to localStorage
    }

    if (routes.length) {
      setSavedRoutes(routes);
      activeRoute = routes[routes.length - 1];
      setSelectedSavedRouteId(getSavedRouteKey(activeRoute, routes.length - 1));
    }

    if (!activeRoute) {
      const fromStorage = localStorage.getItem('@Sergipanidade:lastRoute');
      if (fromStorage) {
        try {
          const storageRoute = JSON.parse(fromStorage) as SavedRoute;
          if (storageRoute?.steps?.length) {
            routes = [...routes, storageRoute];
            setSavedRoutes(routes);
            activeRoute = storageRoute;
            setSelectedSavedRouteId(getSavedRouteKey(storageRoute, routes.length - 1));
          }
        } catch {
          activeRoute = null;
        }
      }
    }

    if (activeRoute) {
      await loadRouteToMap(map, activeRoute);
    }
  };

  const handleSavedRouteChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedKey = event.target.value;
    setSelectedSavedRouteId(selectedKey);
    const selectedRoute = savedRoutes.find((route, index) => getSavedRouteKey(route, index) === selectedKey);
    if (selectedRoute && mapInstance) {
      await loadRouteToMap(mapInstance, selectedRoute);
    }
  };

  const loadPoints = async () => {
    try {
      estabelecimentos = await getLocais();

      const features = estabelecimentos.map((local: any) => {
        const feature = new Feature({
          geometry: new Point(fromLonLat([local.longitude, local.latitude])),
        });
        const rawCategoria = local.categoria;
        const normalizedCategoria =
          typeof rawCategoria === "string"
            ? rawCategoria
            : Array.isArray(rawCategoria)
            ? rawCategoria
                .map((item: any) => item?.nomeCategoria)
                .filter(Boolean)
                .join(", ")
            : rawCategoria?.nomeCategoria ||
              local.categorias?.[0]?.nomeCategoria;
        const markerStyle = new Style({
          image: new Icon({
            anchor: [0.5, 1],
            scale: 0.05,
            src: 'https://cdn-icons-png.flaticon.com/512/684/684908.png'
          }),
        });
        feature.setProperties({
          ...local,
          fotos: Array.isArray(local.fotos) ? local.fotos : [],
          categorias: Array.isArray(local.categorias)
            ? local.categorias
            : Array.isArray(rawCategoria)
            ? rawCategoria
            : local.categorias,
          categoria: normalizedCategoria,
        });
        feature.setStyle(markerStyle);
        feature.set("markerStyle", markerStyle);
        feature.set("isVisible", true);
        return feature;
      });
      vectorSourceRef.current.addFeatures(features);
      updateFeatureVisibility(selectedFilterId);
    } catch (error) {
      console.error("Erro ao carregar pontos:", error);
    }
  };

  return (
    <main className={styles.mapWrapper}>
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        onFilterClick={() => console.log("Filtro clicado!")}
        locais={locaisData}
        selectedFilterId={selectedFilterId}
        onSelectedFilterChange={setSelectedFilterId}
        savedRoutes={savedRoutes}
        selectedSavedRouteId={selectedSavedRouteId}
        onSavedRouteChange={handleSavedRouteChange}
        onItemHover={handleSidebarItemHover}
        onItemLeave={handleSidebarItemLeave}
      />

      <section className={styles.mapArea}>
        <div ref={mapElement} className={styles.mapCanvas}></div>

        {hoveredStore && modalPosition && (
          <StoreDetailsModal
            store={hoveredStore}
            style={{ left: modalPosition.left, top: modalPosition.top }}
            onSeeStore={() => {
              if (hoveredStore?.id) {
                navigate(`/estabelecimentos/${hoveredStore.id}`);
              }
            }}
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                window.clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
              setIsModalHovered(true);
            }}
            onMouseLeave={() => {
              setIsModalHovered(false);
              if (hideTimeoutRef.current) {
                window.clearTimeout(hideTimeoutRef.current);
              }
              hideTimeoutRef.current = window.setTimeout(() => {
                setHoveredStore(null);
                setModalPosition(null);
                hideTimeoutRef.current = null;
              }, 250);
            }}
          />
        )}

        <div className={styles["map-overlay"]}>
          <div className={styles["map-filters"]}>
            {topFilterPills.map((filter) => (
              <button
                key={filter.id}
                className={`${styles["filter-pill"]} ${
                  selectedFilterId === filter.id ? styles.active : ""
                }`}
                onClick={() => handleTopFilterClick(filter.id)}
                type="button"
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className={styles["map-controls"]}>
            <button className={styles["control-btn"]} onClick={handleZoomIn}>
              +
            </button>
            <button className={styles["control-btn"]} onClick={handleZoomOut}>
              -
            </button>

            <button
              className={styles["control-btn"]}
              onClick={handleGoToMyLocation}
              title="Minha Localização"
            >
              🎯
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Mapa;
